"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import type { LocationType } from "@/types/address";

interface Props {
  side: "origin" | "destination";
  value: LocationType | null | undefined;
  onChange: (next: LocationType) => void;
  /** 분해/조립 등으로 INSIDE 외 옵션이 비활성될 때 그 사유 메시지 (있으면 truthy) */
  disabledNonInsideReason?: string | null;
  /** "다음" 클릭 시점에 attempted=true 로 빨간 강조 */
  attempted?: boolean;
  /** 확정 견적 링크 진입 시 읽기전용(변경 불가). 가격 고정을 위해 위치 변경은 새 견적으로 유도 */
  locked?: boolean;
}

const LABELS: Record<"origin" | "destination", Record<LocationType, string>> = {
  origin: { INSIDE: "집안수거", DOOR: "문앞수거", PARKING: "주차장수거" },
  destination: { INSIDE: "집안배송", DOOR: "문앞배송", PARKING: "주차장배송" },
};

export default function Step2LocationTypePicker({
  side,
  value,
  onChange,
  disabledNonInsideReason,
  attempted = false,
  locked = false,
}: Props) {
  const labels = LABELS[side];
  const [warning, setWarning] = useState<string | null>(null);

  const handle = (type: LocationType) => {
    if (locked) return;
    if (type !== "INSIDE" && disabledNonInsideReason) {
      setWarning(disabledNonInsideReason);
      return;
    }
    setWarning(null);
    onChange(type);
  };

  const renderButton = (type: LocationType) => {
    const selected = value === type;
    const disabled = locked ? !selected : type !== "INSIDE" && !!disabledNonInsideReason;
    return (
      <button
        key={type}
        type="button"
        disabled={locked && !selected}
        onClick={() => handle(type)}
        className={cn(
          "flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors",
          selected && "bg-[#1A3A5C] text-white border-[#1A3A5C]",
          !selected && !disabled && "bg-white text-gray-700 border-gray-300 hover:border-[#1A3A5C]",
          disabled && "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
        )}
      >
        {labels[type]}
      </button>
    );
  };

  const missing = attempted && !value;

  return (
    <div className="space-y-2">
      <div
        className={cn(
          "flex gap-2",
          missing && "p-1 border border-red-500 ring-2 ring-red-200 rounded-lg",
        )}
      >
        {renderButton("INSIDE")}
        {renderButton("DOOR")}
        {renderButton("PARKING")}
      </div>
      {missing && (
        <p className="text-sm text-red-500">수거/배송 위치를 선택해주세요</p>
      )}
      {warning && (
        <p className="text-sm text-red-600">{warning}</p>
      )}
      {locked && (
        <p className="text-xs text-gray-500">
          견적 시 선택된 위치입니다. 변경하려면 새 견적을 요청해주세요.
        </p>
      )}
    </div>
  );
}
